import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { X } from 'lucide-react';
import { Button, Checkbox, Dialog, DialogBody, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, IconButton, Label, } from '@wit-support/ui';
import { formatMinor } from '../format.js';
import { SpendFormula, spendButtonAmount, incompleteReasons, spendIncomplete, spendLinesOrTotals, spendTotalText, } from './spend-summary.js';
/** Порог «больше $1 000» по умолчанию (USD, §3.11), если сервер не прислал свой. */
const OVER_1000_DEFAULT_MINOR = 100_000;
/** Текст строки «⚠ Это больше обычного…» (§3.11 «Больше обычного»). */
export function unusualText(u) {
    if (u.reason === 'over-5x-median') {
        return u.medianMinor != null
            ? `⚠ Это больше обычного для шаблона: обычно около ${formatMinor(u.medianMinor, u.currency)} в день`
            : '⚠ Это больше обычного для шаблона';
    }
    const threshold = formatMinor(u.thresholdMinor ?? OVER_1000_DEFAULT_MINOR, u.currency);
    return `⚠ Это больше ${threshold} в день`;
}
const STATUS_MARK = { ok: '✓', error: '⛔', pending: '⏳' };
const STATUS_TEXT = {
    ok: 'готово',
    error: 'не выполнено',
    pending: 'выполняется',
};
/** Длинное неразрывное значение (имя кампании) переносится, а не раздвигает диалог. */
const WRAP = 'min-w-0 [overflow-wrap:anywhere]';
/**
 * Подтверждение действия с деньгами (спека «Конструктор FB v4» §11.0, §11.4):
 * итог в день первой строкой крупно и в подписи кнопки, необязательная
 * галочка, режим прогресса с ходом выполнения и «Остановить». Построен на
 * `Dialog` размера `confirm` (прокручивается тело, подвал прибит; < 640 px —
 * на весь экран, кнопки во всю ширину). Итог, формула, «больше обычного» и
 * строка хода прогона — в прибитой шапке; прокручиваются только подробности
 * (прежний текст, строки прогресса); галочка и причина — над кнопками.
 * Не `ConfirmDialog`/`AlertDialog`:
 * первый принимает только текст, у второго действие закрывает диалог по клику.
 *
 * Фокус: при открытии — «Отмена» (Enter не нажимает основную кнопку); в
 * прогоне — «Остановить» (нет её — список); по итогу — заголовок итога; после
 * закрытия — кнопка, открывшая диалог (`useRestoreFocusOnClose` в `Dialog`).
 * Пока идёт выполнение, закрыть нельзя ни Esc, ни крестиком, ни кликом мимо.
 */
export function SpendConfirmDialog({ open, onOpenChange, title, lines: serverLines, fallbackByCurrency, unusual: serverUnusual, usable, unknownLaunches, notBuilt, rows = [], children, confirmLabel, confirmVariant = 'primary', acknowledgeLabel, blockedReason, onConfirm, pending = false, progress, cancelLabel = 'Отмена', busy = false, busyText, }) {
    const spend = {
        lines: [...serverLines],
        ...(usable !== undefined ? { usable } : {}),
        ...(unknownLaunches !== undefined ? { unknownLaunches } : {}),
        ...(notBuilt ? { notBuilt } : {}),
    };
    const incomplete = spendIncomplete(spend);
    const lines = incomplete ? [] : spendLinesOrTotals(spend, fallbackByCurrency);
    const unusual = incomplete ? null : serverUnusual;
    const amount = spendButtonAmount(lines);
    const [acknowledged, setAcknowledged] = React.useState(false);
    const [submitted, setSubmitted] = React.useState(false);
    // номер текущей попытки: завершение Promise снимает блокировку, только если
    // это всё ещё его попытка (после закрытия/открытия и нового нажатия — нет)
    const attemptRef = React.useRef(0);
    const cancelRef = React.useRef(null);
    const stopRef = React.useRef(null);
    const listRef = React.useRef(null);
    const resultRef = React.useRef(null);
    const doneRef = React.useRef(null);
    const ids = React.useId();
    const reasonId = `${ids}-reason`;
    const ackId = `${ids}-ack`;
    const hasProgress = progress != null;
    // закрыть нельзя: идёт прогон или цепочка «запрос → действие с деньгами»
    const running = progress?.running === true || busy;
    const finished = progress != null && !progress.running && progress.rows.length > 0;
    // галочка подтверждает конкретную сумму: заново при открытии/закрытии и при
    // смене суммы. «Отправлено» — только при открытии/закрытии и переходе в
    // прогресс: смена суммы во время запроса кнопку НЕ разблокирует.
    React.useEffect(() => {
        setAcknowledged(false);
    }, [open, amount]);
    React.useEffect(() => {
        attemptRef.current += 1;
        setSubmitted(false);
    }, [open]);
    React.useEffect(() => {
        if (hasProgress)
            setSubmitted(false);
    }, [hasProgress]);
    // внешний признак запроса закончился (ошибка с тостом и т. п.) — снова можно нажать
    const wasPending = React.useRef(pending);
    React.useEffect(() => {
        if (wasPending.current && !pending)
            setSubmitted(false);
        wasPending.current = pending;
    }, [pending]);
    const focusRunning = React.useCallback(() => {
        (stopRef.current ?? listRef.current)?.focus();
    }, []);
    // вошли в прогон — фокус не теряется вместе с размонтированной кнопкой
    React.useEffect(() => {
        if (open && running)
            focusRunning();
    }, [open, running, focusRunning]);
    // итог выполнения — фокус на его заголовок (нет заголовка — на «Готово»)
    React.useEffect(() => {
        if (open && finished)
            (resultRef.current ?? doneRef.current)?.focus();
    }, [open, finished]);
    const reason = (busy && busyText ? busyText : null) ||
        blockedReason ||
        (submitted || pending ? 'Выполняется…' : null) ||
        (acknowledgeLabel && !acknowledged ? 'Отметьте галочку подтверждения' : null);
    const buttonText = amount ? `${confirmLabel} · ${amount}` : confirmLabel;
    const guardClose = (e) => {
        if (running)
            e.preventDefault();
    };
    const confirm = () => {
        if (reason)
            return;
        const attempt = ++attemptRef.current;
        setSubmitted(true);
        let r;
        try {
            r = onConfirm();
        }
        catch (err) {
            // синхронная ошибка вызывающего — кнопка не остаётся заблокированной
            setSubmitted(false);
            console.error(err);
            return;
        }
        if (r && typeof r.finally === 'function') {
            // и успех, и отказ Promise разблокируют кнопку (отказ показывает вызывающий)
            // устаревшая попытка (диалог закрыт/открыт, нажата снова) кнопку не трогает
            const settle = () => {
                if (attemptRef.current === attempt)
                    setSubmitted(false);
            };
            void r.then(settle, settle);
        }
    };
    return (_jsx(Dialog, { open: open, onOpenChange: (next) => {
            if (!next && running)
                return;
            onOpenChange(next);
        }, children: _jsxs(DialogContent, { size: "confirm", onOpenAutoFocus: (e) => {
                e.preventDefault();
                if (running)
                    focusRunning();
                else if (finished)
                    (resultRef.current ?? doneRef.current)?.focus();
                else
                    cancelRef.current?.focus();
            }, onEscapeKeyDown: guardClose, onPointerDownOutside: guardClose, onInteractOutside: guardClose, children: [_jsxs(DialogHeader, { className: "gap-3", "data-slot": "spend-header", children: [_jsxs("div", { className: "flex flex-row items-start justify-between gap-2", children: [_jsx(DialogTitle, { className: WRAP, children: title }), _jsx(DialogClose, { asChild: true, children: _jsx(IconButton, { label: "\u0417\u0430\u043A\u0440\u044B\u0442\u044C \u043E\u043A\u043D\u043E", variant: "ghost", disabled: running, children: _jsx(X, { "aria-hidden": true, className: "h-4 w-4" }) }) })] }), _jsx(DialogDescription, { asChild: true, children: _jsxs("div", { className: "flex max-h-[45dvh] flex-col gap-2 overflow-y-auto text-sm text-[var(--color-text-primary)]", children: [_jsxs("dl", { className: "grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-4 gap-y-1 sm:grid-cols-[auto_minmax(0,1fr)]", children: [lines.map((line) => (_jsxs(React.Fragment, { children: [_jsx("dt", { className: `${WRAP} text-[var(--color-text-secondary)]`, children: "\u0412 \u0434\u0435\u043D\u044C \u0432\u0441\u0435\u0433\u043E" }), _jsxs("dd", { className: WRAP, children: [_jsx("span", { className: "block text-xl font-semibold tabular-nums", children: spendTotalText(line) }), _jsx(SpendFormula, { line: line })] })] }, line.currency))), incomplete ? (_jsxs(_Fragment, { children: [_jsx("dt", { className: `${WRAP} text-[var(--color-text-secondary)]`, children: "\u0412 \u0434\u0435\u043D\u044C \u0432\u0441\u0435\u0433\u043E" }), _jsxs("dd", { className: WRAP, "data-testid": "spend-confirm-incomplete", children: [_jsx("span", { className: "block font-semibold", children: "\u0438\u0442\u043E\u0433 \u043D\u0435\u043F\u043E\u043B\u043D\u044B\u0439" }), incompleteReasons(spend).map((r) => (_jsx("span", { className: "block text-[var(--color-text-secondary)]", children: r }, r)))] })] })) : null, rows.map((r, i) => (_jsxs(React.Fragment, { children: [_jsx("dt", { className: `${WRAP} text-[var(--color-text-secondary)]`, children: r.label }), _jsx("dd", { className: WRAP, children: r.value })] }, i)))] }), unusual ? (_jsx("p", { className: "font-medium text-[var(--color-state-warning-fg)]", children: unusualText(unusual) })) : null] }) }), progress && progress.rows.length > 0 ? (_jsxs("div", { role: "status", "aria-live": "polite", className: "text-sm", children: [finished && progress.doneTitle ? (_jsx("h3", { ref: resultRef, tabIndex: -1, className: "font-semibold outline-none", children: progress.doneTitle })) : null, running && progress.statusText ? (_jsx("p", { className: "font-medium", children: progress.statusText })) : null] })) : null] }), _jsxs(DialogBody, { "data-slot": "spend-body", children: [children ? (_jsx("div", { className: `${WRAP} text-sm text-[var(--color-text-primary)]`, children: children })) : null, progress && progress.rows.length > 0 ? (_jsx("ul", { ref: listRef, tabIndex: -1, className: "flex flex-col gap-1 text-sm outline-none", "aria-label": "\u0425\u043E\u0434 \u0432\u044B\u043F\u043E\u043B\u043D\u0435\u043D\u0438\u044F", children: progress.rows.map((r) => (_jsxs("li", { className: "flex gap-2", children: [_jsx("span", { "aria-hidden": true, children: STATUS_MARK[r.status] }), _jsxs("span", { className: "sr-only", children: [STATUS_TEXT[r.status], ":"] }), _jsxs("span", { className: WRAP, children: [r.label, r.status !== 'ok' && r.reason ? _jsxs(_Fragment, { children: [" \u2014 ", r.reason] }) : null] })] }, r.id))) })) : null] }), (running && progress?.onStop && progress.stopHint) ||
                    (acknowledgeLabel && !progress) ||
                    (reason && !progress) ? (_jsxs("div", { className: "flex shrink-0 flex-col gap-2 text-sm", "data-slot": "spend-confirm-bar", children: [running && progress?.onStop && progress.stopHint ? (_jsx("p", { className: "text-xs text-[var(--color-text-secondary)]", children: progress.stopHint })) : null, acknowledgeLabel && !progress ? (_jsxs("div", { className: "flex items-start gap-2", children: [_jsx(Checkbox, { id: ackId, checked: acknowledged, onCheckedChange: (v) => setAcknowledged(v === true) }), _jsx(Label, { htmlFor: ackId, className: `${WRAP} text-[var(--color-text-primary)]`, children: acknowledgeLabel })] })) : null, reason && !progress ? (_jsx("span", { id: reasonId, className: `${WRAP} text-[var(--color-text-secondary)]`, children: reason })) : null] })) : null, _jsx(DialogFooter, { className: "flex-wrap items-center", children: progress ? (running ? (progress.onStop ? (_jsx(Button, { ref: stopRef, variant: "secondary", onClick: progress.onStop, children: "\u041E\u0441\u0442\u0430\u043D\u043E\u0432\u0438\u0442\u044C" })) : null) : (_jsx(Button, { ref: doneRef, variant: "secondary", onClick: () => onOpenChange(false), children: "\u0413\u043E\u0442\u043E\u0432\u043E" }))) : (_jsxs(_Fragment, { children: [_jsx(Button, { ref: cancelRef, variant: "ghost", disabled: busy, onClick: () => onOpenChange(false), children: cancelLabel }), _jsx(Button, { variant: confirmVariant, disabled: reason !== null, "aria-describedby": reason ? reasonId : undefined, onClick: confirm, children: buttonText })] })) })] }) }));
}
//# sourceMappingURL=spend-confirm-dialog.js.map